/**
 * Integration product-link API (Integrations 1A page).
 */
import fetchWithAuth from "@/api/secureFetch";

const AGENT_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export interface IntegrationLinkDto {
    id: number;
    externalRef: string;
    externalName: string;
    metadata: Record<string, unknown>;
    projectId: number;
    syncedCount: number | null;
    syncedUnit: string | null;
    lastSyncedAt: string | null;
    syncStatus: "needs_review" | "synced" | "failed";
    lastSyncError: string | null;
}

export interface ContextMeshRefreshNotice {
    context_mesh_refresh?: {
        scheduled: boolean;
        request_id: string;
    };
    warning?: string;
}

export type ProviderId = "jira" | "github" | "gitlab" | "bitbucket" | "azure-devops";
export type IntegrationProviderId = ProviderId | "azure-repos";

export interface VendorConnectionDto {
    provider: IntegrationProviderId;
    vendor: string;
    connected: boolean;
    siteOrOrg: string;
    cloudId: string | null;
    organization: string | null;
    links: IntegrationLinkDto[];
}

export interface CreateLinksRequest {
    provider: ProviderId;
    externalRef: string;
    externalName: string;
    metadata?: Record<string, unknown>;
    projectIds: number[];
}

async function expectOk(response: Response, what: string) {
    if (!response.ok) {
        let detail = "";
        try {
            const body = await response.json();
            if (body && typeof body.error === "string") detail = body.error;
        } catch { /* body was not JSON */ }
        throw new Error(detail || `${what} failed: ${response.status}`);
    }
    return response.json();
}

export async function fetchIntegrationLinks(token: string): Promise<{ vendors: VendorConnectionDto[] }> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links`, token, { method: "GET" });
    return expectOk(response, "Fetching integration links");
}

export async function createIntegrationLinks(req: CreateLinksRequest, token: string): Promise<{ links: IntegrationLinkDto[] } & ContextMeshRefreshNotice> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req)
    });
    return expectOk(response, "Creating integration links");
}

export async function deleteIntegrationLink(linkId: number, token: string): Promise<{ success: boolean } & ContextMeshRefreshNotice> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links/${linkId}`, token, { method: "DELETE" });
    return expectOk(response, "Deleting integration link");
}

export async function syncIntegrationLink(
    linkId: number,
    token: string,
    onProgress: (message: string) => void = () => undefined
): Promise<{ link: IntegrationLinkDto } & ContextMeshRefreshNotice> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links/${linkId}/sync`, token, {
        method: "POST"
    });
    if (!response.ok) {
        return expectOk(response, "Synchronizing integration link");
    }
    if (!response.body) throw new Error("Synchronization stream was unavailable");

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
        const { done, value } = await reader.read();
        buffer = (buffer + decoder.decode(value, { stream: !done })).replace(/\r\n/g, "\n");

        let boundary = buffer.indexOf("\n\n");
        while (boundary !== -1) {
            const frame = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            boundary = buffer.indexOf("\n\n");

            const event = frame.match(/^event:\s*(.+)$/m)?.[1]?.trim();
            const dataLines = frame.match(/^data:\s*(.*)$/gm) || [];
            if (!event || dataLines.length === 0) continue;

            let payload: Record<string, unknown>;
            try {
                payload = JSON.parse(dataLines.map(line => line.replace(/^data:\s*/, "")).join("\n"));
            } catch {
                continue;
            }

            if (event === "progress") {
                if (typeof payload.message === "string") onProgress(payload.message);
                continue;
            }
            if (event === "error") {
                throw new Error(typeof payload.error === "string" ? payload.error : "Synchronization failed");
            }
            if (event === "complete") {
                if (!payload.link || typeof payload.link !== "object") {
                    throw new Error("Synchronization completed without an updated link");
                }
                return payload as unknown as { link: IntegrationLinkDto } & ContextMeshRefreshNotice;
            }
        }
        if (done) break;
    }

    throw new Error("Synchronization ended before completion");
}

export async function unlinkAllForRef(provider: string, externalRef: string, token: string): Promise<{ success: boolean; deleted: number } & ContextMeshRefreshNotice> {
    const qs = `provider=${encodeURIComponent(provider)}&externalRef=${encodeURIComponent(externalRef)}`;
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/integrations/links?${qs}`, token, { method: "DELETE" });
    return expectOk(response, "Unlinking");
}

export async function listGitRepositories(
    provider: "github" | "gitlab" | "bitbucket" | "azure_repos",
    token: string,
    page = 1,
    perPage = 100
): Promise<{ repositories: Array<{ name: string; full_name?: string; clone_url?: string; url?: string; default_branch?: string }> }> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/git/${provider}/repositories`, token, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page, perPage })
    });
    return expectOk(response, "Listing repositories");
}

export async function disconnectGitProvider(
    provider: "github" | "gitlab" | "bitbucket",
    token: string
): Promise<{ success: boolean } & ContextMeshRefreshNotice> {
    const response = await fetchWithAuth(`${AGENT_BASE_URL}/api/v1/git/${provider}/credentials/disconnect`, token, { method: "POST" });
    return expectOk(response, "Disconnecting");
}
